import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { X } from "lucide-react";
import { fetchFoodsPage } from "../../services/catalog.api";
import { FOOD_TYPE, normalizeFoodTypeParam } from "../../services/normalizers";
import FoodCard from "../shared/FoodCard";
import LoadMore from "../shared/LoadMore";
import CategorySection from "./sections/CategorySection";
import FilterSection from "./sections/FilterSection";
import FoodSearch from "./sections/FoodSearch";
import "./styles/LeftFilter.css";

const FOOD_TYPE_LABELS = {
  offer: "🔥 Offer", [FOOD_TYPE.READY_TO_EAT]: "⚡ Ready to Eat",
  [FOOD_TYPE.PRE_ORDER]: "☕ Pre-Order", [FOOD_TYPE.E_SHOP]: "✅ E-Shop",
};

export default function AllFoods() {
  const [search, setSearch] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const location = useLocation();
  const [filter, setFilter] = useState({
    foodType: "all", mealType: [], priceMin: 1, priceMax: 5000,
    categoryId: "", kitchens: [],
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryId = params.get("categoryId") || "";
    const foodType = normalizeFoodTypeParam(params.get("foodType") || "all");
    // Only replace the filter when the URL actually differs — a new object
    // every render would change the query key and refetch page 1 needlessly.
    setFilter(prev =>
      prev.categoryId === categoryId && prev.foodType === foodType
        ? prev
        : { ...prev, categoryId, foodType },
    );
  }, [location]);

  // Typing shouldn't fire a request per keystroke; the debounced value is what
  // the query key watches, so changing a filter starts a fresh page 1.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  const {
    data,
    isPending,
    error,
    isRefetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["allFoods", debouncedSearch, filter],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      fetchFoodsPage({
        search: debouncedSearch || undefined,
        foodType: filter.foodType,
        // The API takes one meal type; the filter UI is single-select in practice.
        mealType: filter.mealType[0],
        minPrice: filter.priceMin,
        maxPrice: filter.priceMax,
        categoryId: filter.categoryId || undefined,
        chefIds: filter.kitchens,
        page: pageParam,
        limit: 24,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextPage : undefined,
  });

  const allFoods = useMemo(
    () => (data?.pages || []).flatMap((p) => p.items),
    [data],
  );
  // "Offer" and multi-kitchen filters narrow each page in the browser, so the
  // server's total no longer describes what is on screen.
  const isFiltered = data?.pages?.[0]?.filtered;
  const resultCount = isFiltered
    ? allFoods.length
    : (data?.pages?.[0]?.pagination?.total ?? allFoods.length);

  if (error) toast.error(error?.message);

  const activeFilters = [
    filter.foodType !== "all" && { key: "foodType", label: FOOD_TYPE_LABELS[filter.foodType] || filter.foodType },
    filter.priceMin > 1 || filter.priceMax < 5000
      ? { key: "price", label: `৳${filter.priceMin}–${filter.priceMax}` }
      : null,
    filter.kitchens.length > 0 && { key: "kitchens", label: `${filter.kitchens.length} Kitchen${filter.kitchens.length > 1 ? "s" : ""}` },
  ].filter(Boolean);

  const removeFilter = (key) => {
    if (key === "foodType") setFilter(p => ({ ...p, foodType: "all" }));
    if (key === "price")    setFilter(p => ({ ...p, priceMin: 1, priceMax: 5000 }));
    if (key === "kitchens") setFilter(p => ({ ...p, kitchens: [] }));
  };

  return (
    <div className="home-content-shell page-navbar-space pb-5">

      {/* Search bar */}
      <FoodSearch
        setIsFilterOpen={setIsFilterOpen}
        search={search}
        setSearch={setSearch}
        activeFilterCount={activeFilters.length}
      />

      {/* Active filter chips — mobile + desktop */}
      {activeFilters.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
          {activeFilters.map(f => (
            <span key={f.key} style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              background: "#eff6ff", border: "1px solid #bfdbfe",
              color: "#1d4ed8", borderRadius: 20,
              padding: "3px 10px", fontSize: 12, fontWeight: 600,
              fontFamily: "'Inter', sans-serif",
            }}>
              {f.label}
              <button
                onClick={() => removeFilter(f.key)}
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", color: "#1d4ed8" }}
              >
                <X size={12} strokeWidth={2.5} />
              </button>
            </span>
          ))}
          <button
            onClick={() => setFilter({ foodType: "all", mealType: [], priceMin: 1, priceMax: 5000, categoryId: "", kitchens: [] })}
            style={{ background: "none", border: "none", padding: "3px 4px", fontSize: 12, color: "#9ca3af", cursor: "pointer", fontFamily: "'Inter', sans-serif" }}
          >
            Clear all
          </button>
        </div>
      )}

      {/* Category chips */}
      <CategorySection selectedCategoryId={filter.categoryId} />

      {/* Main layout */}
      <div className="af-main-layout">

        {/* Filter sidebar (desktop only) */}
        <FilterSection
          filter={filter}
          setFilter={setFilter}
          isFilterOpen={isFilterOpen}
          setIsFilterOpen={setIsFilterOpen}
        />

        {/* Food grid */}
        <div className="af-grid-area">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <h5 style={{ fontWeight: 800, fontSize: 18, margin: 0, fontFamily: "'Inter', sans-serif", color: "#1a1a2e" }}>
              All Foods
            </h5>
            {!isPending && !isRefetching && (
              <span style={{ fontSize: 12, color: "#9ca3af", fontFamily: "'Inter', sans-serif" }}>
                {isFiltered && hasNextPage
                  ? `${resultCount}+ results`
                  : `${resultCount} results`}
              </span>
            )}
          </div>

          {allFoods?.length === 0 && !isPending && !isRefetching && (
            <div style={{ textAlign: "center", padding: "60px 20px", fontFamily: "'Inter', sans-serif" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🍽️</div>
              <p style={{ fontSize: 15, fontWeight: 600, color: "#374151", margin: "0 0 4px" }}>No foods found</p>
              <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>Try adjusting your filters or search</p>
            </div>
          )}

          <div className="all-food-grid">
            {isPending || isRefetching
              ? [1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} height={260} borderRadius={8} />)
              : allFoods.map((food) => <FoodCard key={food._id} item={food} />)
            }
          </div>

          {!isPending && !isRefetching && (
            <LoadMore
              hasMore={hasNextPage}
              isLoading={isFetchingNextPage}
              onClick={() => fetchNextPage()}
              label="Load more food"
            />
          )}
        </div>
      </div>

      {/* Mobile overlay for filter drawer */}
      {isFilterOpen && (
        <div
          onClick={() => setIsFilterOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9998 }}
        />
      )}
    </div>
  );
}
